import { requireContext } from 'common';
import { RoleConfiguration, StaffRole, ViewSuggestion, StudentEntityHistory, roleDashboard as api } from 'sis-app/api'
import { Accessor, Setter, createContext, createEffect, createSignal } from 'solid-js';
import { useViewPreferences } from 'st-app';

export const DASHBOARD_VIEW_GU = "A646985F-76D4-4D5D-A16A-19ED5E567F80";
const [prefs, setPrefs] = useViewPreferences(DASHBOARD_VIEW_GU);

export class RoleDashboardContext {
    configuration: Accessor<RoleConfiguration | undefined>;
    private setConfiguration: Setter<RoleConfiguration | undefined>;

    suggestions: Accessor<ViewSuggestion[]>;
    private setSuggestions: Setter<ViewSuggestion[]>;

    enrActivityCount: Accessor<number>;
    private setEnrActivityCount: Setter<number>;

    userName: Accessor<string>;
    setUserName: Setter<string>;

    selectedRole: Accessor<StaffRole>;
    setSelectedRole: Setter<StaffRole>;

    showLocateStudentModal: Accessor<boolean>;
    setShowLocateStudentModal: Setter<boolean>;

    showQuickRefGuides: Accessor<boolean>;
    setShowQuickRefGuides: Setter<boolean>;

    viewDrawerRequest: Accessor<string | object>;
    setViewDrawerRequest: Setter<string | object>;

    viewDrawerTitle: Accessor<string>;
    setViewDrawerTitle: Setter<string>;

    launchDetached: Accessor<boolean>;
    setLaunchDetached: Setter<boolean>;

    refreshTrigger: Accessor<number>;
    private setRefreshTrigger: Setter<number>;

    constructor() {
        [this.showLocateStudentModal, this.setShowLocateStudentModal] = createSignal(false);
        [this.showQuickRefGuides, this.setShowQuickRefGuides] = createSignal(false);
        [this.configuration, this.setConfiguration] = createSignal<RoleConfiguration>();
        [this.suggestions, this.setSuggestions] = createSignal<ViewSuggestion[]>([]);
        [this.enrActivityCount, this.setEnrActivityCount] = createSignal(0);
        [this.userName, this.setUserName] = createSignal('');
        [this.viewDrawerRequest, this.setViewDrawerRequest] = createSignal<string | object>('');
        [this.viewDrawerTitle, this.setViewDrawerTitle] = createSignal('');
        [this.launchDetached, this.setLaunchDetached] = createSignal(false);
        [this.refreshTrigger, this.setRefreshTrigger] = createSignal(0);

        {
            const raw = prefs["SelectedRole"];
            let initialRole: StaffRole;

            if (/^\d+$/.test(raw)) {
                initialRole = parseInt(raw, 10) as StaffRole;
            } else {
                initialRole = (StaffRole[raw as keyof typeof StaffRole] ?? StaffRole.Unknown) as StaffRole;
            }

            [this.selectedRole, this.setSelectedRole] = createSignal<StaffRole>(initialRole);
        }

        createEffect(() => {
            const config = this.configuration();

            if (config) {
                const selRole = this.selectedRole();
                if (selRole == StaffRole.Unknown || !config.userRoles.includes(selRole)) {
                    const role = config.userRoles[0];
                    this.setSelectedRole(role ?? StaffRole.Unknown);
                }
            }
        });

        createEffect((prev) => {
            if (this.selectedRole() !== prev) {
                setPrefs('SelectedRole', this.selectedRole()?.toString() ?? '');
            }
        }, this.selectedRole());

        api.GetConfiguration().then(this.setConfiguration);
        api.GetUserSuggestions().then(this.setSuggestions);
        api.GetUserInfo().then((user) => this.setUserName(user.name ?? ''));
        api.GetEnrollmentActivityCount().then(this.setEnrActivityCount);
    }

    sortedActions(actions: string[]): string[] {
        return actions.map(id => this.configuration()?.actions.find(act => act.ID == id))
            .toSorted((a, b) => {
                if (a?.description && b?.description) {
                    return a.description.toLocaleLowerCase().localeCompare(b.description.toLocaleLowerCase())
                }
                return 0;
            })
            .map(desc => desc?.ID)
            .filter(id => !!id) as string[];
    }

    triggerRefresh() {
        this.setRefreshTrigger(prev => prev + 1);
        api.GetEnrollmentActivityCount().then(this.setEnrActivityCount);
    }
}

export const RoleDashboardContextJsx = createContext<RoleDashboardContext>();
export const requireRoleDashboardContext = () => {
    return requireContext(RoleDashboardContextJsx);
}
